import React, { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, Instagram, Mail } from "lucide-react";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/how-it-works", label: "How It Works" },
  { to: "/boxes", label: "Boxes", full: "Subscription Boxes" },
  { to: "/partnerships", label: "Partnerships", full: "Brand Partnerships" },
  { to: "/waitlist", label: "Waitlist" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${scrolled ? "bg-ivory/95 backdrop-blur-xl border-b border-taupe/40 shadow-sm" : "bg-gradient-to-b from-ivory/50 via-ivory/20 to-transparent backdrop-blur-[2px]"}`}
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 flex items-center justify-between h-20">
        <Link data-testid="brand-logo" to="/" className="flex items-center gap-2 group">
          <span className="font-serif text-2xl tracking-tight text-matte">Crowned <span className="italic text-gold">Essence</span></span>
        </Link>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
          {NAV.map(n => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${(n.full || n.label).toLowerCase().replace(/\s+/g,'-')}`}
              className={({isActive}) =>
                `text-[11px] uppercase tracking-luxe-sm font-light link-gold whitespace-nowrap ${isActive ? "text-gold" : "text-matte hover:text-gold"}`}
              end={n.to === "/"}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <Link
            data-testid="header-cta-waitlist"
            to="/waitlist"
            className="px-6 py-3 bg-matte text-gold border border-gold/40 text-[11px] uppercase tracking-luxe-sm hover:bg-black transition-colors"
          >
            Join Waitlist
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          aria-label="Open menu"
          onClick={() => setOpen(v => !v)}
          className="lg:hidden text-matte p-2"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-ivory border-t border-taupe/40">
          <div className="px-6 py-6 flex flex-col gap-4">
            {NAV.map(n => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`mobile-nav-${(n.full || n.label).toLowerCase().replace(/\s+/g,'-')}`}
                className={({isActive}) => `text-sm uppercase tracking-luxe-sm ${isActive ? "text-gold" : "text-matte"}`}
                end={n.to === "/"}
              >
                {n.label}
              </NavLink>
            ))}
            <Link
              data-testid="mobile-cta-waitlist"
              to="/waitlist"
              className="mt-2 inline-block px-6 py-3 bg-matte text-gold text-[11px] uppercase tracking-luxe-sm w-fit"
            >
              Join Waitlist
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

export function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-matte text-ivory/90 mt-32">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-20 grid md:grid-cols-4 gap-12">
        <div className="md:col-span-2">
          <div className="font-serif text-3xl">Crowned <span className="italic text-gold">Essence</span></div>
          <p className="mt-4 max-w-md text-sm text-ivory/60 leading-relaxed font-light">
            Luxury self-care, curated with purpose. Celebrating Black-owned excellence — one intentional routine at a time.
          </p>
          <div className="mt-6 flex items-center gap-4">
            <a data-testid="footer-instagram" href="https://instagram.com/crownedessence5" target="_blank" rel="noreferrer" className="w-10 h-10 border border-gold/40 flex items-center justify-center hover:bg-gold/10 transition-colors">
              <Instagram size={16} className="text-gold" />
            </a>
            <a data-testid="footer-email" href="mailto:crownedessence5@gmail.com" className="w-10 h-10 border border-gold/40 flex items-center justify-center hover:bg-gold/10 transition-colors">
              <Mail size={16} className="text-gold" />
            </a>
          </div>
        </div>

        <div>
          <div className="text-[11px] uppercase tracking-luxe-sm text-gold mb-4">Quick Links</div>
          <ul className="space-y-2 text-sm text-ivory/70">
            <li><Link to="/about" className="hover:text-gold">About</Link></li>
            <li><Link to="/how-it-works" className="hover:text-gold">How It Works</Link></li>
            <li><Link to="/boxes" className="hover:text-gold">Subscription Boxes</Link></li>
            <li><Link to="/partnerships" className="hover:text-gold">Brand Partnerships</Link></li>
            <li><Link to="/faq" className="hover:text-gold">FAQ</Link></li>
            <li><Link to="/contact" className="hover:text-gold">Contact</Link></li>
          </ul>
        </div>

        <div>
          <div className="text-[11px] uppercase tracking-luxe-sm text-gold mb-4">Newsletter</div>
          <p className="text-sm text-ivory/60 mb-3">Be first to know when we launch.</p>
          <Link to="/waitlist" data-testid="footer-join-waitlist" className="inline-block px-6 py-3 bg-gold text-matte text-[11px] uppercase tracking-luxe-sm hover:bg-gold-soft transition-colors">Join the Waitlist</Link>
        </div>
      </div>

      <div className="border-t border-ivory/10">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-6 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="text-xs text-ivory/50">© {new Date().getFullYear()} Crowned Essence. All rights reserved.</div>
          <div className="flex items-center gap-6 text-xs text-ivory/50">
            <a href="#" className="hover:text-gold">Privacy Policy</a>
            <a href="#" className="hover:text-gold">Terms</a>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  return (
    <div className="min-h-screen bg-ivory text-matte">
      <Header />
      <main data-testid="site-main">{children}</main>
      <Footer />
    </div>
  );
}
